'use server';

import { createClient, createAdminClient } from '@/lib/supabase/server';
import { revalidatePath } from 'next/cache';
import { generateRequestId, generateWalkInProjectId } from '@/lib/idgen';
import { table25 } from '@/lib/supabase/inventory25';
import { notifyStudents, notifyRole } from '@/actions/notifications';
import { assertRequestDatesNotBlocked, assertRevisionAllowed, consumeAcceptOneRevision } from '@/actions/requestLeadTimeGates';
import { getEmailsByUserIds } from '@/lib/authUsers';
import { sendRequestApprovedEmail, sendRequestNeedsReviewEmail, sendRequestDeclinedEmail, sendRequestCancelledEmail } from '@/lib/requestEmails';
import type { TablesUpdate } from '@/lib/supabase/database.types';
import { isStaffRole } from '@/lib/roles';
import { requirePermission } from '@/lib/permissions';

export interface ApprovedRequestScheduleRow {
  id: string;
  project: string;
  datesFrom: string | null;
  datesTo: string | null;
  pickupTime: string | null;
  returnTime: string | null;
}

/**
 * Approved-but-not-yet-checked-out requests, for the TV Dashboard's Project
 * Schedule widget — staff wanted upcoming load-out/load-in dates visible as
 * soon as a project is approved, not only once someone runs it through
 * staff checkout. `status` flips to 'checked_out' the moment createCheckout
 * links a requestId (see checkout.ts), so filtering on 'approved' here
 * naturally drops off the schedule at that point — no de-dup needed against
 * active-checkout data the dashboard already shows separately.
 */
export async function getApprovedRequestsForSchedule(): Promise<ApprovedRequestScheduleRow[]> {
  await requirePermission('checkout_checkin');
  const supabase = await createAdminClient();
  const { data, error } = await supabase
    .from('requests')
    .select('id, project, dates_from, dates_to, pickup_time, return_time')
    .eq('status', 'approved')
    .order('dates_from', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []).map(r => ({
    id: r.id,
    project: r.project || 'Untitled project',
    datesFrom: r.dates_from,
    datesTo: r.dates_to,
    pickupTime: r.pickup_time,
    returnTime: r.return_time,
  }));
}

/** Best-effort — email failure must never affect the request-status change that triggered it. */
async function emailRecipients(ids: string[]): Promise<string[]> {
  try {
    const admin = await createAdminClient();
    const emailMap = await getEmailsByUserIds(admin, ids);
    return [...emailMap.values()].filter(Boolean);
  } catch (e) {
    console.error('[requests] email lookup failed (non-fatal):', e);
    return [];
  }
}

const PATHS = ['/staff/requests', '/student/requests', '/staff/dashboard', '/student/dashboard'];

// Was the real server-side enforcement point for "can't request a barcode-keyed
// V1/V2 item that's in repair or retired" (confirmed live: an item was requested
// 21s after being marked "Shipped to Vendor" because nothing server-side checked
// repair_status/item_status before this existed). items_v1_legacy is now fully
// dropped, so this permanently no-ops — kept as an explicit no-op (rather than
// removing the 5 call sites below) so the intent stays documented at each call
// site. Inventory 2.5's equivalent check is in inventory25requests.ts, against
// `items.is_retired`/`items.status` by item_id, not barcode — genuinely separate
// code, not something this function ever covered for 2.5 items.
async function assertNoRepairedOrRetiredItems(
  _barcodes: string[],
  _supabase: Awaited<ReturnType<typeof createClient>>
): Promise<void> {
  return;
}

export async function submitRequest(data: {
  studentId: string;
  project: string;
  projectType: string;
  isClassAssignment: boolean;
  notes: string;
  datesFrom: string;
  datesTo: string;
  items: { barcode: string; itemName: string; qty: number; isWaitlisted?: boolean; variantSelection?: Record<string, number> | null }[];
  collaboratorIds?: string[];
  studentCollaboratorIds?: string[];
  requestType?: string;
}) {
  const supabase = await createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  // Staff submit on behalf of a student via the staff cart (staff/cart/page.tsx),
  // so studentId can legitimately differ from the caller — but a student caller
  // must only ever be able to submit as themselves, not an arbitrary studentId.
  const { data: callerProfile } = await supabase.from('profiles').select('role').eq('id', user.id).single();
  const studentId = isStaffRole(callerProfile?.role) ? data.studentId : user.id;
  await assertRequestDatesNotBlocked(data.datesFrom, data.datesTo);
  await assertNoRepairedOrRetiredItems(data.items.map(i => i.barcode), supabase);
  const requestId = await generateRequestId(supabase);

  const { data: req, error: reqError } = await supabase
    .from('requests')
    .insert({
      id:                       requestId,
      student_id:               studentId,
      project:                  data.project,
      project_type:             data.projectType,
      is_class_assignment:      data.isClassAssignment,
      notes:                    data.notes,
      dates_from:               data.datesFrom,
      dates_to:                 data.datesTo,
      status:                   'pending',
      collaborator_ids:         data.collaboratorIds ?? [],
      student_collaborator_ids: data.studentCollaboratorIds ?? [],
      request_type:             data.requestType ?? 'student',
    })
    .select('id')
    .single();
  if (reqError) throw new Error(reqError.message);

  const { error: itemError } = await supabase.from('request_items').insert(
    data.items.map(i => ({
      request_id:    req.id,
      barcode:       i.barcode,
      item_name:     i.itemName,
      qty:           i.qty,
      status:        'pending',
      added_by:      studentId,
      is_waitlisted: i.isWaitlisted ?? false,
      variant_selection: i.variantSelection ?? null,
    }))
  );
  if (itemError) throw new Error(itemError.message);

  // Barcode-keyed legacy-package auto-approval (fires when package mode is on, every
  // item is covered by the student's assigned package, all are available via
  // items_v1_legacy, and none are waitlisted) removed 2026-08-18 — its availability
  // source, items_v1_legacy, is fully dropped, so this had already stopped firing in
  // practice (the DB read always came back empty). Requests now always fall through
  // to normal manual staff review, same net effect as before removal. A 2.5
  // item_id-based equivalent, if wanted, is a product decision for whoever owns
  // package auto-approval, not assumed here.

  PATHS.forEach(p => revalidatePath(p));
  return req.id as string;
}

/** Stable identity for diffing one item line across an edit — `item_id` when present
 * (every 2.5-typed line), falling back to `barcode` (V1/V2), then the item name as a
 * last resort so two null-barcode/null-item_id lines never collide into one key. */
function itemDiffKey(i: { itemId: string | null; barcode: string | null; itemName: string }): string {
  return i.itemId ?? i.barcode ?? `name:${i.itemName}`;
}

/** Human-readable "what changed" for the staff queue's version-history entry —
 * "2 added, 1 removed", "qty changed on 1 item", etc. */
function summarizeItemDiff(
  before: { itemId: string | null; barcode: string | null; itemName: string; qty: number }[],
  after: { itemId: string | null; barcode: string | null; itemName: string; qty: number }[]
): string {
  const beforeByKey = new Map(before.map((i) => [itemDiffKey(i), i]));
  const afterByKey = new Map(after.map((i) => [itemDiffKey(i), i]));
  let added = 0, removed = 0, changed = 0;
  for (const [key, a] of afterByKey) {
    const b = beforeByKey.get(key);
    if (!b) added++;
    else if (b.qty !== a.qty) changed++;
  }
  for (const key of beforeByKey.keys()) {
    if (!afterByKey.has(key)) removed++;
  }
  const parts: string[] = [];
  if (added > 0) parts.push(`${added} added`);
  if (removed > 0) parts.push(`${removed} removed`);
  if (changed > 0) parts.push(`qty changed on ${changed} item${changed !== 1 ? 's' : ''}`);
  return parts.length > 0 ? parts.join(', ') : 'No item changes';
}

export async function updateRequest(
  requestId: string,
  changes: {
    project?: string;
    projectType?: string;
    isClassAssignment?: boolean;
    course?: string;
    notes?: string;
    requestedDates?: { from: string; to: string };
    items?: { barcode?: string | null; itemId?: string | null; itemName: string; qty: number; groupId?: string | null; groupLabel?: string | null }[];
  },
  editNote = 'Edited by student'
) {
  const supabase = await createClient();

  const touchesInventoryOrDates = Boolean(changes.requestedDates || changes.items);
  if (touchesInventoryOrDates) {
    await assertRevisionAllowed(requestId);
  }
  if (changes.requestedDates) {
    await assertRequestDatesNotBlocked(changes.requestedDates.from, changes.requestedDates.to);
  }

  const { data: current, error: fetchError } = await supabase
    .from('requests')
    .select('activity_log, version_history, version, version_number, dates_from, dates_to, notes')
    .eq('id', requestId)
    .single();
  if (fetchError) throw new Error(fetchError.message);

  const datesChanged = Boolean(
    changes.requestedDates &&
      (changes.requestedDates.from !== (current.dates_from ?? '') ||
        changes.requestedDates.to !== (current.dates_to ?? ''))
  );

  // Notes / project name only — do not kick status back to pending (freeze still allows those).
  const metadataOnly = !touchesInventoryOrDates;

  // Real before/after item diff + a bumped version number for actual inventory/date
  // edits (2026-08-25, direct ask: "if a student updates their request by adding
  // items, removing items... this should update the version" + "staff request queue
  // should have a version number and notes as to what was done"). Previously this
  // function pushed `{editedAt, note}` into version_history and never touched `version`
  // at all — the staff UI's "Student Edits" section (already built, reads
  // `v.version`/`v.summary`/`v.itemsBefore`/`v.itemsAfter`) was rendering blank/broken
  // for every real student edit because nothing ever wrote that shape. Metadata-only
  // edits (project name/notes, no items/dates touched) don't bump version — matches
  // "adding/removing items" being the actual trigger, not any edit at all.
  let itemsBefore: { itemId: string | null; barcode: string | null; itemName: string; qty: number }[] = [];
  if (changes.items) {
    const { data: existingItems, error: existingErr } = await supabase
      .from('request_items')
      .select('item_id, barcode, item_name, qty')
      .eq('request_id', requestId);
    if (existingErr) throw new Error(existingErr.message);
    itemsBefore = ((existingItems ?? []) as any[]).map((i) => ({
      itemId: i.item_id ?? null,
      barcode: i.barcode ?? null,
      itemName: i.item_name,
      qty: i.qty ?? 1,
    }));
  }

  const activityLog = (current?.activity_log as any[]) ?? [];
  const versionHistory = (current?.version_history as any[]) ?? [];
  const currentVersion = Math.max((current?.version as number) ?? 1, (current?.version_number as number) ?? 1);

  const updateData: Record<string, any> = {
    updated_at:      new Date().toISOString(),
    activity_log:    [...activityLog, {
      action: datesChanged
        ? `${editNote} — dates changed; all lines reset to pending for re-review`
        : editNote,
      at: new Date().toISOString(),
    }],
  };
  // Note text riding along on the same edit that bumped a version (items/dates changed)
  // was silently dropped from version_history — the staff "Student Edits" panel could
  // show what items/dates changed on that revision, but not that the note also changed
  // at the same time. A notes-only edit still doesn't bump the version on its own (see
  // metadataOnly above) — this only captures notes when a version entry is already
  // being written for another reason.
  const notesChanged = changes.notes !== undefined && changes.notes !== (current.notes ?? '');

  if (touchesInventoryOrDates) {
    const itemsAfter = (changes.items ?? itemsBefore).map((i) => ({
      itemId: i.itemId ?? null,
      barcode: i.barcode ?? null,
      itemName: i.itemName,
      qty: i.qty,
    }));
    const summary = changes.items ? summarizeItemDiff(itemsBefore, itemsAfter) : 'Dates changed';
    const nextVersion = currentVersion + 1;
    updateData.version = nextVersion;
    updateData.version_number = nextVersion;
    updateData.version_history = [
      ...versionHistory,
      {
        version: nextVersion,
        summary,
        itemsBefore,
        itemsAfter,
        datesBefore: datesChanged ? { from: current.dates_from, to: current.dates_to } : null,
        datesAfter: datesChanged ? changes.requestedDates : null,
        notesBefore: notesChanged ? (current.notes ?? '') : null,
        notesAfter: notesChanged ? changes.notes : null,
        changedAt: new Date().toISOString(),
      },
    ];
  }
  if (!metadataOnly) {
    updateData.status = 'pending';
  }
  // Date move: strip project approval so students cannot keep approved gear while
  // shifting the window (William 2026-08-11).
  if (datesChanged) {
    updateData.status = 'pending';
    updateData.project_approved = false;
    updateData.needs_student_review = false;
    updateData.approved_version = null;
  }
  if (changes.project !== undefined)           updateData.project = changes.project;
  if (changes.projectType !== undefined)       updateData.project_type = changes.projectType;
  if (changes.isClassAssignment !== undefined) updateData.is_class_assignment = changes.isClassAssignment;
  if (changes.course !== undefined)            updateData.course = changes.course;
  if (changes.notes !== undefined)             updateData.notes = changes.notes;
  if (changes.requestedDates) {
    updateData.dates_from = changes.requestedDates.from;
    updateData.dates_to   = changes.requestedDates.to;
  }

  if (changes.items) {
    await assertNoRepairedOrRetiredItems(changes.items.map(i => i.barcode ?? ''), supabase);
  }

  const { error: updateError } = await supabase.from('requests').update(updateData as TablesUpdate<'requests'>).eq('id', requestId);
  if (updateError) throw new Error(updateError.message);

  if (datesChanged && !changes.items) {
    const { error: rependError } = await supabase
      .from('request_items')
      .update({ status: 'pending', staff_note: null })
      .eq('request_id', requestId);
    if (rependError) throw new Error(rependError.message);
  }

  if (changes.items) {
    const { error: delError } = await supabase.from('request_items').delete().eq('request_id', requestId);
    if (delError) throw new Error(delError.message);

    const { error: itemError } = await supabase.from('request_items').insert(
      changes.items.map(i => ({
        request_id:  requestId,
        barcode:     i.barcode ?? null,
        item_id:     i.itemId ?? null,
        item_name:   i.itemName,
        qty:         i.qty,
        status:      'pending',
        staff_note:  null,
        group_id:    i.groupId ?? null,
        group_label: i.groupLabel ?? null,
      })) as any
    );
    if (itemError) throw new Error(itemError.message);
  }

  if (touchesInventoryOrDates) {
    await consumeAcceptOneRevision(requestId);
  }

  PATHS.forEach(p => revalidatePath(p));
}

export async function approveRequestItems(
  requestId: string,
  itemStatuses: { id: string; status: 'approved' | 'declined'; staffNote?: string }[]
) {
  const supabase = await createClient();

  // For items being approved, verify qty_available at the moment of approval.
  // This catches double-booking when two staff approve requests for the same item.
  // Keyed by request_items.id (unique, always present) rather than barcode —
  // Supply items have a null barcode by design, and multiple null-barcode
  // lines in the same request would otherwise collide on that key. Items
  // with no barcode have no inventory row to cross-check availability
  // against here, so they're approved as requested rather than skipped —
  // their availability was already checked at submission time.
  const toApprove = itemStatuses.filter(i => i.status === 'approved').map(i => i.id);
  if (toApprove.length > 0) {
    // `item_id` (2_5_013_transaction_tables_item_id.sql, 2026-08-05) is how a 2.5-typed
    // request line identifies itself. The parallel `barcode`-keyed availability lookup
    // against items_v1_legacy (V1/V2 lines) was removed 2026-08-18 — that table is
    // fully dropped, so any remaining barcode-only request_items row (pre-cutover data,
    // if any) simply has no inventory row to check against, same as it always resolved
    // to once the table stopped existing.
    // `item_id` cast — database.types.ts hasn't been regenerated since
    // 2_5_013_transaction_tables_item_id.sql added this column (regenerating breaks
    // V1/V2 code not ready for a schema diff yet, same reason table25() exists).
    const { data: reqItemsRaw } = await supabase
      .from('request_items')
      .select('id, item_id, qty')
      .eq('request_id', requestId)
      .in('id', toApprove);
    const reqItems = (reqItemsRaw ?? []) as unknown as { id: string; item_id: string | null; qty: number | null }[];

    const itemIds = reqItems.map(ri => ri.item_id).filter((id): id is string => id != null);
    const items25Table = await table25('items');
    const { data: items25 } = itemIds.length > 0
      ? await items25Table.select('id, qty_available').in('id', itemIds)
      : { data: [] as { id: string; qty_available: number | null }[] };
    const availByItemId: Record<string, number> = {};
    for (const i of (items25 ?? []) as { id: string; qty_available: number | null }[]) availByItemId[i.id] = i.qty_available ?? 0;

    for (const ri of reqItems) {
      const avail = ri.item_id != null ? availByItemId[ri.item_id] : undefined;
      if (avail === undefined) continue; // no inventory row to check against
      if (avail < (ri.qty ?? 1)) {
        // Auto-decline items that can no longer be fulfilled
        const idx = itemStatuses.findIndex(i => i.id === ri.id);
        if (idx !== -1) {
          itemStatuses[idx].status = 'declined';
          itemStatuses[idx].staffNote = `Auto-declined: only ${avail} available at time of approval`;
        }
      }
    }
  }

  const updateResults = await Promise.all(
    itemStatuses.map(({ id, status, staffNote }) =>
      supabase
        .from('request_items')
        .update({ status, staff_note: staffNote ?? null })
        .eq('request_id', requestId)
        .eq('id', id)
    )
  );
  const failedUpdate = updateResults.find(r => r.error);
  if (failedUpdate?.error) throw new Error(failedUpdate.error.message);

  const { data: items } = await supabase
    .from('request_items')
    .select('status')
    .eq('request_id', requestId);

  // Line-item approve/decline is staff's private scratchpad while reviewing.
  // Direct product ask (William, 2026-08-09): students must NOT see a live stream of
  // each click — they only learn the outcome when staff commits via
  // markProjectApproved ("Approve for checkout") or sendBackForRevision.
  // So this function never writes status='approved'/'declined' on the parent row
  // (and never locks the request). At most it marks the request `partial` so the
  // staff queue can tell "started reviewing" from "untouched pending". Student UI
  // further masks item-level statuses until that commit (see student/requests).
  const statuses = items?.map(i => i.status) ?? [];
  const anyReviewed = statuses.some(s => s === 'approved' || s === 'declined');
  const overall = anyReviewed ? 'partial' : 'pending';

  const { error: overallError } = await supabase.from('requests').update({
    status: overall,
  }).eq('id', requestId);
  if (overallError) throw new Error(overallError.message);
  PATHS.forEach(p => revalidatePath(p));
}

/**
 * Path A — put a (possibly partially declined) request into the approved pool.
 * Stamps approved_version, flags needs_student_review when declined lines remain,
 * and notifies owner + collaborators.
 */
export async function markProjectApproved(requestId: string, staffId: string) {
  const supabase = await createClient();
  // `as any` — database.types.ts hasn't been regenerated since 065_requests_
  // project_id.sql added this column (same reason table25() exists for 2.5 tables;
  // regenerating types for one new column on a heavily-typed table like `requests`
  // isn't worth doing out of band here).
  const { data: req, error: fetchError } = await (supabase as any)
    .from('requests')
    .select('id, project, student_id, collaborator_ids, student_collaborator_ids, version_number, activity_log, project_id, pickup_time, return_time, dates_from, dates_to')
    .eq('id', requestId)
    .single();
  if (fetchError) throw new Error(fetchError.message);

  // Project id is assigned once, on first approval — not regenerated on re-approval
  // after a student revision (project_approved can flip false→true repeatedly across
  // versions; the project's identity shouldn't move each time).
  const projectId: string = req.project_id || await generateWalkInProjectId(supabase);

  const { data: items } = await supabase
    .from('request_items')
    .select('id, status')
    .eq('request_id', requestId);
  const hasDeclines = (items ?? []).some(i => i.status === 'declined');
  const hasApproved = (items ?? []).some(i => i.status === 'approved');
  if (!hasApproved) {
    throw new Error('Approve at least one item before putting this project in the checkout pool.');
  }

  const version = req.version_number ?? 1;
  const log = (req.activity_log as any[]) ?? [];
  const { error } = await (supabase as any)
    .from('requests')
    .update({
      project_approved: true,
      status: 'approved',
      approved_by: staffId || null,
      approved_version: version,
      needs_student_review: hasDeclines,
      project_id: projectId,
      activity_log: [...log, {
        type: 'project_approved',
        action: hasDeclines
          ? `Approved for checkout as v${version} — some items declined; students notified to review`
          : `Approved for checkout as v${version}`,
        timestamp: new Date().toISOString(),
      }],
    })
    .eq('id', requestId);
  if (error) throw new Error(error.message);

  const recipients = [
    req.student_id,
    ...(req.collaborator_ids ?? []),
    ...(req.student_collaborator_ids ?? []),
  ];
  const project = req.project || 'your request';

  if (hasDeclines) {
    await notifyStudents(
      recipients,
      'partial_approval',
      `"${project}" was partially approved (v${version}). Review declined items — remove them or add replacements.`,
      requestId
    );
    const emails = await emailRecipients(recipients);
    if (emails.length > 0) {
      await sendRequestNeedsReviewEmail({
        emails,
        project,
        reason: 'Some items were declined — review and remove them or add replacements before checkout.',
      });
    }
  } else {
    // Previously silent — a clean approval (no declines) told nobody. Every
    // approval now notifies, since this is the moment the student can act
    // on it (go pick up gear), not just an internal status flip.
    await notifyStudents(
      recipients,
      'project_approved',
      `"${project}" was approved (v${version}) and is ready for checkout.`,
      requestId
    );
    const emails = await emailRecipients(recipients);
    if (emails.length > 0) {
      await sendRequestApprovedEmail({
        emails,
        project,
        schedule: {
          pickupTime: req.pickup_time,
          returnTime: req.return_time,
          datesFrom: req.dates_from,
          datesTo: req.dates_to,
        },
      });
    }
  }

  PATHS.forEach(p => revalidatePath(p));
}

/**
 * Path B — critical declines: do NOT put in approved pool. Students must revise first.
 */
export async function sendBackForRevision(requestId: string, staffId: string, note: string) {
  const supabase = await createClient();
  const { data: req, error: fetchError } = await supabase
    .from('requests')
    .select('id, project, student_id, collaborator_ids, student_collaborator_ids, activity_log, pickup_time, return_time, dates_from, dates_to')
    .eq('id', requestId)
    .single();
  if (fetchError) throw new Error(fetchError.message);

  // Staff queue / student tab semantics (William, 2026-08-09):
  // - Partial (student sees Pending) = sent back while any lines are still open
  //   (approved and/or pending) alongside declines — e.g. declined a few, left others
  //   pending. Must NOT use hasApproved-only; that dumped "pending+declined" into
  //   Declined (project "lovely").
  // - Declined = every line is declined (whole project rejected).
  // Mid-review scratchpad still uses status=partial without needs_student_review.
  const { data: items } = await supabase
    .from('request_items')
    .select('id, status')
    .eq('request_id', requestId);
  const hasOpenLines = (items ?? []).some(
    i => i.status === 'approved' || i.status === 'pending'
  );
  const nextStatus = hasOpenLines ? 'partial' : 'declined';

  const log = (req.activity_log as any[]) ?? [];
  const reason = note.trim() || 'Staff asked you to revise before this project can be approved.';
  const { error } = await supabase
    .from('requests')
    .update({
      status: nextStatus,
      project_approved: false,
      needs_student_review: true,
      approved_by: staffId || null,
      approved_version: null,
      activity_log: [...log, {
        type: 'sent_back',
        action: hasOpenLines
          ? `Sent back for revision (partial) — ${reason}`
          : `Sent back for revision (declined) — ${reason}`,
        timestamp: new Date().toISOString(),
      }],
    })
    .eq('id', requestId);
  if (error) throw new Error(error.message);

  const recipients = [
    req.student_id,
    ...(req.collaborator_ids ?? []),
    ...(req.student_collaborator_ids ?? []),
  ];
  await notifyStudents(
    recipients,
    'needs_revision',
    `"${req.project || 'Your request'}" was sent back for revision. ${reason}`,
    requestId
  );
  const emails = await emailRecipients(recipients);
  if (emails.length > 0) {
    const project = req.project || 'Your request';
    if (nextStatus === 'declined') {
      await sendRequestDeclinedEmail({
        emails,
        project,
        reason,
        schedule: {
          pickupTime: req.pickup_time,
          returnTime: req.return_time,
          datesFrom: req.dates_from,
          datesTo: req.dates_to,
        },
      });
    } else {
      await sendRequestNeedsReviewEmail({ emails, project, reason });
    }
  }

  PATHS.forEach(p => revalidatePath(p));
}

/**
 * Student clears declined lines and/or adds replacement items after Path A.
 * Remove-only → stays approved. New pending lines → back to staff as partial.
 */
export async function resolveDeclinedItems(
  requestId: string,
  opts: {
    removeDeclinedIds: string[];
    replacements?: { barcode?: string | null; itemId?: string | null; itemName: string; qty: number }[];
  }
) {
  const supabase = await createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');

  const { data: req, error: fetchError } = await supabase
    .from('requests')
    .select('id, student_id, collaborator_ids, student_collaborator_ids, version_number, activity_log, project_approved, status')
    .eq('id', requestId)
    .single();
  if (fetchError) throw new Error(fetchError.message);

  const allowed = new Set([
    req.student_id,
    ...(req.collaborator_ids ?? []),
    ...(req.student_collaborator_ids ?? []),
  ]);
  if (!allowed.has(user.id)) throw new Error('You can only update requests you own or collaborate on.');

  const replacements = opts.replacements ?? [];
  // Declined cleanup (remove only) stays allowed inside the freeze; replacements need a revision slot.
  if (replacements.length > 0) {
    await assertRevisionAllowed(requestId);
  }

  const { data: currentItems } = await supabase
    .from('request_items')
    .select('id, barcode, item_name, qty, status, item_id')
    .eq('request_id', requestId);

  const declinedIds = new Set(
    (currentItems ?? []).filter(i => i.status === 'declined').map(i => i.id)
  );
  const toRemove = opts.removeDeclinedIds.filter(id => declinedIds.has(id));
  if (toRemove.length > 0) {
    const { error: delError } = await supabase
      .from('request_items')
      .delete()
      .eq('request_id', requestId)
      .in('id', toRemove);
    if (delError) throw new Error(delError.message);
  }

  if (replacements.length > 0) {
    // item_id added by 2_5_013 — cast until types regen covers every call site.
    const { error: insError } = await supabase.from('request_items').insert(
      replacements.map(i => ({
        request_id: requestId,
        barcode: i.barcode ?? null,
        item_id: i.itemId ?? null,
        item_name: i.itemName,
        qty: i.qty,
        status: 'pending',
        added_by: user.id,
      })) as any
    );
    if (insError) throw new Error(insError.message);
  }

  const nextVersion = (req.version_number ?? 1) + 1;
  const log = (req.activity_log as any[]) ?? [];
  const addedCount = replacements.length;
  const removedCount = toRemove.length;

  let nextStatus: string = req.status ?? 'approved';
  let needsStudentReview = false;
  if (addedCount > 0) {
    nextStatus = 'partial'; // approved lines remain; new lines need staff
    needsStudentReview = false;
  } else {
    // Check remaining declined
    const { data: left } = await supabase
      .from('request_items')
      .select('status')
      .eq('request_id', requestId);
    const stillDeclined = (left ?? []).some(i => i.status === 'declined');
    const anyApproved = (left ?? []).some(i => i.status === 'approved');
    needsStudentReview = stillDeclined;
    // Students must not escalate to `approved` via the client (DB trigger blocks it).
    // Path A remove-only keeps `approved` when already there; otherwise stay/partial.
    if (anyApproved && req.status === 'approved') {
      nextStatus = 'approved';
    } else if (anyApproved || stillDeclined) {
      nextStatus = 'partial';
    } else {
      nextStatus = 'pending';
    }
  }

  const { error: updError } = await supabase
    .from('requests')
    .update({
      version: nextVersion,
      version_number: nextVersion,
      previous_items: currentItems ?? [],
      status: nextStatus,
      needs_student_review: needsStudentReview,
      project_approved: addedCount > 0 ? false : (req.project_approved ?? true),
      updated_at: new Date().toISOString(),
      activity_log: [...log, {
        type: 'student_resolve_declines',
        action: addedCount > 0
          ? `Updated to v${nextVersion}: removed ${removedCount} declined, added ${addedCount} for staff review`
          : `Updated to v${nextVersion}: removed ${removedCount} declined item${removedCount === 1 ? '' : 's'}`,
        timestamp: new Date().toISOString(),
      }],
    })
    .eq('id', requestId);
  if (updError) throw new Error(updError.message);

  if (addedCount > 0) {
    await consumeAcceptOneRevision(requestId);
    await notifyRole(null, 'request_updated', `updated a partially approved request (v${nextVersion}) — new items need review`, requestId);
  }

  PATHS.forEach(p => revalidatePath(p));
}

// Keyed by request_items.id, not barcode — Supply items have a null barcode
// by design, so multiple barcode-less lines in the same request would
// otherwise collide on the same pulled_items key (checking one would mark
// all of them pulled). Same fix already applied to approve/decline this
// session (src/actions/requests.ts's approveRequestItems).
//
// Rewritten (2026-08-20) to call the mark_item_pulled() RPC instead of a client-side
// select-merge-update — that read-modify-write was a real lost-update race once
// multiple staff could be working the same pull sheet at once (direct ask: "checkout
// needs to be option 2 as well... multiple names of the staff doing a part in it").
// Two staff marking two different items within the same round-trip window used to be
// able to silently clobber each other's write; the RPC merges atomically in Postgres.
// staffId/staffName attribute the pull (requests.pulled_by), shown in PullSheetModal.
export async function markItemPulled(requestId: string, itemId: string, pulled: boolean, staffId?: string | null, staffName?: string | null) {
  const supabase = await createClient();
  // mark_item_pulled + requests.pull_participants/pulled_by (2_5_041) aren't in the
  // generated database.types.ts yet — cast, same pattern as auditSessionsTable() etc.
  // in staff/audit/page.tsx.
  const { error } = await (supabase as any).rpc('mark_item_pulled', {
    p_request_id: requestId,
    p_item_id: itemId,
    p_pulled: pulled,
    p_user_id: staffId ?? null,
    p_user_name: staffName ?? 'Staff',
  });
  if (error) throw new Error(error.message);
  PATHS.forEach(p => revalidatePath(p));
}

// Tracks who's currently working a request's pull sheet (requests.pull_participants) —
// joined when a staff member opens PullSheetModal, mirrors collaborator_ids' shape/
// pattern above. Same read-modify-write shape as addCollaborator (fine here: joining
// happens once per staff member per pull sheet open, not at scan speed).
export async function joinPullSheet(requestId: string, staffId: string) {
  const supabase = await createClient();
  const { data: req, error: fetchError } = await (supabase as any)
    .from('requests')
    .select('pull_participants')
    .eq('id', requestId)
    .single();
  if (fetchError) throw new Error(fetchError.message);

  const current = (req.pull_participants as string[]) ?? [];
  if (current.includes(staffId)) return;

  const { error } = await (supabase as any)
    .from('requests')
    .update({ pull_participants: [...current, staffId] })
    .eq('id', requestId);
  if (error) throw new Error(error.message);

  PATHS.forEach(p => revalidatePath(p));
}

/** Staff-only Pull Sheet scratch notes (2_5_052, 2026-08-26 direct ask) — "so that on
 * the day of checkout, we could review these notes and make changes on the day."
 * Deliberately a plain overwrite (last-write-wins, no history/versioning) — this is a
 * scratch pad for the day of the pull, not a record that needs a diff trail the way
 * `notes`/version_history does. `pull_notes` isn't in generated database.types.ts yet,
 * same cast pattern as joinPullSheet just above. */
export async function updatePullNotes(requestId: string, notes: string): Promise<void> {
  await requirePermission('checkout_checkin');
  const supabase = await createClient();
  const { error } = await (supabase as any)
    .from('requests')
    .update({ pull_notes: notes.trim() || null })
    .eq('id', requestId);
  if (error) throw new Error(error.message);
  PATHS.forEach(p => revalidatePath(p));
}

/** Staff queue: change a pending line's qty and/or send a student-visible note
 * (including the "explain why you need this" preset). */
export async function updateRequestItemStaffEdit(
  requestId: string,
  itemId: string,
  qty: number,
  staffNote: string,
): Promise<void> {
  await requirePermission('view_request_queue');
  const q = Math.floor(Number(qty));
  if (!Number.isFinite(q) || q < 1) throw new Error('Quantity must be at least 1');
  const note = staffNote.trim() || null;
  const supabase = await createClient();

  const { data: row, error: fetchErr } = await supabase
    .from('request_items')
    .select('id, item_name, qty, status')
    .eq('id', itemId)
    .eq('request_id', requestId)
    .single();
  if (fetchErr || !row) throw new Error('Request item not found');
  if (row.status !== 'pending') throw new Error('Only pending lines can be edited');

  const { error } = await supabase
    .from('request_items')
    .update({ qty: q, staff_note: note })
    .eq('id', itemId)
    .eq('request_id', requestId);
  if (error) throw new Error(error.message);

  const { data: req } = await supabase
    .from('requests')
    .select('student_id, collaborator_ids, student_collaborator_ids, project, activity_log')
    .eq('id', requestId)
    .single();
  if (req) {
    const log = Array.isArray(req.activity_log) ? req.activity_log : [];
    const prevQty = row.qty ?? 1;
    const { error: logErr } = await supabase
      .from('requests')
      .update({
        activity_log: [
          ...log,
          {
            type: 'item_edited',
            action: `Updated ${row.item_name}: qty ${prevQty} → ${q}${note ? ' · note sent' : ''}`,
            timestamp: new Date().toISOString(),
          },
        ],
      } as TablesUpdate<'requests'>)
      .eq('id', requestId);
    if (logErr) console.error('[requests] activity_log after item edit failed (non-fatal):', logErr.message);

    if (note) {
      const recipients = [
        req.student_id,
        ...(req.collaborator_ids ?? []),
        ...(req.student_collaborator_ids ?? []),
      ];
      const project = req.project || 'your request';
      await notifyStudents(
        recipients,
        'staff_item_note',
        `Staff left a note on "${row.item_name}" in "${project}".`,
        requestId,
      );
    }
  }

  PATHS.forEach(p => revalidatePath(p));
}

export async function updateRequestTimes(requestId: string, pickupTime: string | null, returnTime: string | null) {
  const supabase = await createClient();
  const { error } = await supabase
    .from('requests')
    .update({ pickup_time: pickupTime, return_time: returnTime })
    .eq('id', requestId);
  if (error) throw new Error(error.message);
  PATHS.forEach(p => revalidatePath(p));
}

export async function cancelRequest(requestId: string, reason: string, staffName: string) {
  const supabase = await createClient();
  const { data } = await supabase.from('requests').select('id, project, student_id, collaborator_ids, student_collaborator_ids, activity_log').eq('id', requestId).single();
  const log = (data?.activity_log as any[]) ?? [];
  const { error } = await supabase.from('requests').update({
    status: 'cancelled',
    activity_log: [...log, {
      type:      'cancellation',
      note:      reason || 'No reason provided',
      staff:     staffName,
      timestamp: new Date().toISOString(),
    }],
  }).eq('id', requestId);
  if (error) throw new Error(error.message);
  if (data) {
    const recipients = [
      data.student_id,
      ...(data.collaborator_ids ?? []),
      ...(data.student_collaborator_ids ?? []),
    ].filter(Boolean);
    const project = data.project || 'A request';
    await notifyStudents(recipients, 'request_cancelled', `"${project}" was cancelled by staff.`, requestId);
    const emails = await emailRecipients(recipients);
    if (emails.length > 0) {
      await sendRequestCancelledEmail({ emails, project, by: 'staff' });
    }
  }
  PATHS.forEach(p => revalidatePath(p));
}

/**
 * Student-owned cancel — owner only. Allowed until checkout starts (not checked_out)
 * and before staff has pulled gear. Matches lead-time freeze copy: name/notes edits
 * and cancel remain available when review-needing edits are locked.
 */
export async function cancelStudentRequest(requestId: string, reason = '') {
  const supabase = await createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');

  const { data: req, error: fetchError } = await supabase
    .from('requests')
    .select('id, project, student_id, collaborator_ids, student_collaborator_ids, status, activity_log, pulled_items')
    .eq('id', requestId)
    .single();
  if (fetchError) throw new Error(fetchError.message);

  if (req.student_id !== user.id) {
    throw new Error('Only the request owner can cancel this request.');
  }

  const status = req.status ?? '';
  if (status === 'cancelled') throw new Error('This request is already cancelled.');
  if (status === 'checked_out') {
    throw new Error('This request is already checked out — contact staff if you need help.');
  }
  if (!['pending', 'partial', 'approved', 'declined'].includes(status)) {
    throw new Error('This request can no longer be cancelled.');
  }

  const pulled = (req.pulled_items as Record<string, boolean> | null) ?? {};
  if (Object.values(pulled).some(Boolean)) {
    throw new Error('Staff has already pulled gear for this request — contact them to cancel.');
  }

  const note = reason.trim() || 'Cancelled by student';
  const log = (req.activity_log as any[]) ?? [];
  const { error } = await supabase.from('requests').update({
    status: 'cancelled',
    needs_student_review: false,
    activity_log: [...log, {
      type: 'cancellation',
      action: note,
      note,
      by: user.id,
      timestamp: new Date().toISOString(),
    }],
  }).eq('id', requestId);
  if (error) throw new Error(error.message);

  const project = req.project || 'A request';
  const recipients = [
    req.student_id,
    ...(req.collaborator_ids ?? []),
    ...(req.student_collaborator_ids ?? []),
  ].filter(Boolean);
  const collaborators = recipients.filter((id: string) => id !== user.id);
  if (collaborators.length > 0) {
    await notifyStudents(
      collaborators,
      'request_cancelled',
      `"${project}" was cancelled by the owner.`,
      requestId
    );
  }
  await notifyRole(null, 'request_cancelled', `cancelled "${project}"`, requestId);
  const emails = await emailRecipients(recipients);
  if (emails.length > 0) {
    await sendRequestCancelledEmail({ emails, project, by: 'owner' });
  }

  PATHS.forEach(p => revalidatePath(p));
}

export async function resubmitRequest(
  requestId: string,
  data: {
    project: string;
    projectType: string;
    isClassAssignment: boolean;
    notes: string;
    datesFrom: string;
    datesTo: string;
    items: { barcode?: string | null; itemId?: string | null; itemName: string; qty: number; groupId?: string | null; groupLabel?: string | null }[];
  }
) {
  const supabase = await createClient();
  await assertNoRepairedOrRetiredItems(data.items.map(i => i.barcode ?? ''), supabase);
  await assertRevisionAllowed(requestId);
  await assertRequestDatesNotBlocked(data.datesFrom, data.datesTo);

  const { data: existing, error: fetchError } = await supabase
    .from('requests')
    .select('activity_log, version, locked_at, dates_from, dates_to')
    .eq('id', requestId)
    .single();
  if (fetchError) throw new Error(fetchError.message);

  if (existing?.locked_at) {
    throw new Error('This request has been approved and cannot be modified.');
  }

  const datesChanged =
    data.datesFrom !== (existing.dates_from ?? '') || data.datesTo !== (existing.dates_to ?? '');

  const { data: currentItems } = await supabase
    .from('request_items')
    .select('barcode, item_id, item_name, qty, group_id, group_label, status')
    .eq('request_id', requestId);

  const log = (existing?.activity_log as any[]) ?? [];
  const nextVersion = (existing?.version ?? 1) + 1;

  const { error: reqError } = await supabase
    .from('requests')
    .update({
      project:               data.project,
      project_type:          data.projectType,
      is_class_assignment:   data.isClassAssignment,
      notes:                 data.notes,
      dates_from:            data.datesFrom,
      dates_to:              data.datesTo,
      status:                'pending',
      project_approved:      false,
      needs_student_review:  false,
      approved_version:      null,
      version:               nextVersion,
      version_number:        nextVersion,
      previous_items:        currentItems ?? [],
      updated_at:            new Date().toISOString(),
      activity_log: [...log, {
        type:      'student_edit',
        action:    datesChanged
          ? `Request resubmitted as v${nextVersion} (dates changed — all lines pending re-review)`
          : `Request updated to v${nextVersion}`,
        timestamp: new Date().toISOString(),
      }],
    })
    .eq('id', requestId);
  if (reqError) throw new Error(reqError.message);

  const { error: delError } = await supabase
    .from('request_items')
    .delete()
    .eq('request_id', requestId);
  if (delError) throw new Error(delError.message);

  if (data.items.length === 0) {
    throw new Error('Keep at least one item when resubmitting.');
  }

  const { error: itemError } = await supabase.from('request_items').insert(
    data.items.map(i => ({
      request_id:  requestId,
      barcode:     i.barcode ?? null,
      item_id:     i.itemId ?? null,
      item_name:   i.itemName,
      qty:         i.qty,
      status:      'pending',
      group_id:    i.groupId ?? null,
      group_label: i.groupLabel ?? null,
    })) as any
  );
  if (itemError) throw new Error(itemError.message);

  await consumeAcceptOneRevision(requestId);

  // Previously silent — staff had no way to know a sent-back request was
  // revised short of polling the queue. In-app only: staff already watch
  // the queue during active review, so this doesn't need email.
  await notifyRole(null, 'request_resubmitted', `resubmitted "${data.project || 'a request'}" (v${nextVersion}) for review`, requestId);

  PATHS.forEach(p => revalidatePath(p));
}

export async function appendItemsToRequest(
  requestId: string,
  newItems: { barcode: string; itemName: string; qty: number }[]
) {
  const supabase = await createClient();
  await assertNoRepairedOrRetiredItems(newItems.map(i => i.barcode), supabase);
  await assertRevisionAllowed(requestId);

  const { data: existing, error: fetchError } = await supabase
    .from('requests')
    .select('activity_log, version')
    .eq('id', requestId)
    .single();
  if (fetchError) throw new Error(fetchError.message);

  const log = (existing?.activity_log as any[]) ?? [];
  const nextVersion = (existing?.version ?? 1) + 1;

  // Check existing item statuses BEFORE inserting new ones
  const { data: currentItems } = await supabase
    .from('request_items')
    .select('status')
    .eq('request_id', requestId);
  const hasApproved = currentItems?.some(i => i.status === 'approved') ?? false;

  const { error: itemError } = await supabase.from('request_items').insert(
    newItems.map(i => ({
      request_id: requestId,
      barcode:    i.barcode,
      item_name:  i.itemName,
      qty:        i.qty,
      status:     'pending',
    }))
  );
  if (itemError) throw new Error(itemError.message);

  const newStatus = hasApproved ? 'partial' : 'pending';

  const { error: reqError } = await supabase
    .from('requests')
    .update({
      status:       newStatus,
      version:      nextVersion,
      updated_at:   new Date().toISOString(),
      activity_log: [...log, {
        type:      'student_edit',
        action:    `${newItems.length} item(s) added (v${nextVersion})`,
        timestamp: new Date().toISOString(),
      }],
    })
    .eq('id', requestId);
  if (reqError) throw new Error(reqError.message);

  await consumeAcceptOneRevision(requestId);
  PATHS.forEach(p => revalidatePath(p));
}

export async function addCollaborator(requestId: string, collaboratorId: string) {
  const supabase = await createClient();

  const { data: req, error: fetchError } = await supabase
    .from('requests')
    .select('student_id, collaborator_ids')
    .eq('id', requestId)
    .single();
  if (fetchError) throw new Error(fetchError.message);

  const current = (req.collaborator_ids as string[]) ?? [];
  if (req.student_id === collaboratorId || current.includes(collaboratorId)) return;

  const { error } = await supabase
    .from('requests')
    .update({ collaborator_ids: [...current, collaboratorId] })
    .eq('id', requestId);
  if (error) throw new Error(error.message);

  PATHS.forEach(p => revalidatePath(p));
}

export async function removeCollaborator(requestId: string, collaboratorId: string) {
  const supabase = await createClient();

  const { data: req, error: fetchError } = await supabase
    .from('requests')
    .select('collaborator_ids')
    .eq('id', requestId)
    .single();
  if (fetchError) throw new Error(fetchError.message);

  const updated = ((req.collaborator_ids as string[]) ?? []).filter(id => id !== collaboratorId);

  const { error } = await supabase
    .from('requests')
    .update({ collaborator_ids: updated })
    .eq('id', requestId);
  if (error) throw new Error(error.message);

  PATHS.forEach(p => revalidatePath(p));
}

export async function addMissingItemNote(checkoutId: string, barcode: string, note: string) {
  const supabase = await createClient();
  const { data } = await supabase
    .from('checkouts')
    .select('missing_item_notes')
    .eq('id', checkoutId)
    .single();
  const current = (data?.missing_item_notes as Record<string, string>) ?? {};
  const { error } = await supabase
    .from('checkouts')
    .update({ missing_item_notes: { ...current, [barcode]: note } })
    .eq('id', checkoutId);
  if (error) throw new Error(error.message);
  revalidatePath('/staff/checkin');
}

// Replace a request's items with the current cart contents, incrementing version.
// Used by the "Add to existing request" cart flow (Feature A).
// NOTE: Requires DB columns: request_items.is_waitlisted (bool), request_items.added_by (text),
//       requests.previous_items (jsonb), requests.locked_at (timestamptz).
export async function addCartToRequest(
  requestId: string,
  items: { barcode: string; itemName: string; qty: number; isWaitlisted?: boolean; variantSelection?: Record<string, number> | null }[],
  studentId: string
) {
  const supabase = await createClient();
  await assertNoRepairedOrRetiredItems(items.map(i => i.barcode), supabase);

  const { data: existing, error: fetchError } = await supabase
    .from('requests')
    .select('activity_log, version, locked_at')
    .eq('id', requestId)
    .single();
  if (fetchError) throw new Error(fetchError.message);

  if ((existing as any)?.locked_at) {
    throw new Error('This request has been approved and cannot be modified.');
  }

  const log = (existing?.activity_log as any[]) ?? [];
  const nextVersion = (existing?.version ?? 1) + 1;

  // Append cart lines — never wipe existing request_items (William 2026-08-11:
  // "adding onto a request" was deleting everything and leaving only the new lines).
  const { data: currentItems } = await supabase
    .from('request_items')
    .select('barcode, item_name, qty, status')
    .eq('request_id', requestId);
  const hasApproved = (currentItems ?? []).some(i => i.status === 'approved');

  const { error: itemError } = await supabase.from('request_items').insert(
    items.map(i => ({
      request_id:    requestId,
      barcode:       i.barcode,
      item_name:     i.itemName,
      qty:           i.qty,
      status:        'pending',
      added_by:      studentId,
      is_waitlisted: i.isWaitlisted ?? false,
      variant_selection: i.variantSelection ?? null,
    }))
  );
  if (itemError) throw new Error(itemError.message);

  const { error: reqError } = await supabase
    .from('requests')
    .update({
      status:               hasApproved ? 'partial' : 'pending',
      ...(hasApproved ? { project_approved: false, approved_version: null } : {}),
      needs_student_review: false,
      version:              nextVersion,
      previous_items:       currentItems ?? [],
      updated_at:           new Date().toISOString(),
      activity_log:   [...log, {
        type:      'student_edit',
        action:    `${items.length} item(s) added from cart (v${nextVersion})`,
        timestamp: new Date().toISOString(),
      }],
    })
    .eq('id', requestId);
  if (reqError) throw new Error(reqError.message);

  PATHS.forEach(p => revalidatePath(p));
}
